import { useQuery } from 'react-query';

import type {
  AgentGateway,
  PatientChatMessagePage,
  PatientChatTurn,
  PatientChatUnanswered,
  PatientConversationSummary,
} from '@mummssoftware/utils';

import { CONVERSATION_MESSAGES_PAGE_SIZE } from '../constants';
import { maiaQueryKeys } from '../queryClient';
import type {
  AnswerDetails,
  ChatMessage,
  ConversationQuestion,
} from '../types';
import { secondsBetween } from '../utils/formatters';
import { errorMessage, isGatewayError, unwrap } from '../utils/gatewayError';
import defaults from '../translations/en.json';

const answerDetails = (answer: PatientChatTurn): AnswerDetails => ({
  responseDetail: answer.responseDetail,
  tokensUsed: answer.tokensUsed,
  promptTokens: answer.promptTokens,
  cachedTokens: answer.cachedTokens,
  candidatesTokens: answer.candidatesTokens,
  thoughtsTokens: answer.thoughtsTokens,
  inputTokens: answer.inputTokens,
  outputTokens: answer.outputTokens,
  questionsCostUsd: answer.estimatedCostUsd,
  cacheCostUsd: answer.cacheCostUsd,
  totalEstimatedCostUsd: answer.totalEstimatedCostUsd,
});

/**
 * Questions that got no answer, as rows among the answered ones: in time
 * order (startsAt is when each was asked), why, and what each spent. A
 * negative id keeps them apart from the answers' message ids.
 */
export const unansweredQuestions = (
  unanswered: PatientChatUnanswered[],
): ConversationQuestion[] =>
  unanswered.map((row) => ({
    id: -row.requestId,
    startsAt: row.askedAt,
    question: row.question || '',
    answer: '',
    durationSeconds: null,
    outcome: row.outcome,
    details: {
      tokensUsed: row.tokensUsed,
      promptTokens: row.promptTokens,
      cachedTokens: row.cachedTokens,
      candidatesTokens: row.candidatesTokens,
      thoughtsTokens: row.thoughtsTokens,
      inputTokens: row.inputTokens,
      outputTokens: row.outputTokens,
      questionsCostUsd: row.estimatedCostUsd,
      cacheCostUsd: row.cacheCostUsd,
      totalEstimatedCostUsd: row.totalEstimatedCostUsd,
    },
  }));

/** Stored messages as the transcript shows them; answers carry their length, time and tokens. */
export const toChatMessages = (stored: PatientChatTurn[]): ChatMessage[] =>
  stored.map((message) =>
    message.role === 'assistant'
      ? {
          id: String(message.id),
          role: 'assistant',
          content: message.content,
          answer: answerDetails(message),
        }
      : { id: String(message.id), role: 'user', content: message.content },
  );

/**
 * One row per answered question. The gateway stores a question and its answer
 * together, in that order, so each answer pairs with the message before it.
 * Each question's duration runs to the next question; the first starts at the
 * conversation's start and the last ends where the conversation does (Done, or
 * the last answer while not ended), so they add up to its durationSeconds.
 */
export const conversationQuestions = (
  stored: PatientChatTurn[],
  conversation: PatientConversationSummary,
): ConversationQuestion[] => {
  const pairs = stored.reduce<
    Array<{ question: PatientChatTurn; answer: PatientChatTurn }>
  >((found, message, index) => {
    const previous = stored[index - 1];
    if (message.role === 'assistant' && previous?.role === 'user') {
      found.push({ question: previous, answer: message });
    }
    return found;
  }, []);
  const end = conversation.endedAt || conversation.lastMessageAt;
  return pairs.map(({ question, answer }, index) => {
    const startsAt = index === 0 ? conversation.startedAt : question.createdAt;
    return {
      id: answer.id,
      startsAt,
      question: question.content,
      answer: answer.content,
      durationSeconds: secondsBetween(
        startsAt,
        index < pairs.length - 1 ? pairs[index + 1].question.createdAt : end,
      ),
      details: answerDetails(answer),
    };
  });
};

/** One question and its answer, as the full-size view shows them. */
export const questionMessages = (row: ConversationQuestion): ChatMessage[] => [
  { id: `${row.id}-q`, role: 'user', content: row.question },
  {
    id: String(row.id),
    role: 'assistant',
    content: row.answer,
    answer: row.details,
  },
];

type UseConversationMessagesResult = {
  /** The conversation's messages in order (question, answer, question, ...). */
  stored: PatientChatTurn[];
  isLoading: boolean;
  error: string | null;
};

const NO_MESSAGES: PatientChatTurn[] = [];

/**
 * Every stored question and answer of one conversation, read only. Used by the
 * full-size past view and a history row's questions, which share the cached
 * result (queryClient.ts): opening a conversation whose row was expanded, or
 * opening it again, sends no request, and the tile and expanded copies send
 * one between them. An ended conversation never changes, so it is kept; one
 * still open (someone may still be asking) reloads each time it is shown.
 */
export const useConversationMessages = (
  agentGateway: AgentGateway,
  conversation: PatientConversationSummary | null,
): UseConversationMessagesResult => {
  const conversationId = conversation?.id ?? null;
  const query = useQuery(
    maiaQueryKeys.messages(conversationId ?? -1),
    async () =>
      unwrap<PatientChatMessagePage>(
        await agentGateway.listPatientChatMessages(conversationId as number, {
          size: CONVERSATION_MESSAGES_PAGE_SIZE,
        }),
      )?.content || NO_MESSAGES,
    {
      enabled: conversationId != null,
      staleTime: conversation?.endedAt ? Infinity : 0,
    },
  );

  let error: string | null = null;
  if (query.error) {
    error =
      isGatewayError(query.error) && query.error.status === 403
        ? defaults.pastForbidden
        : errorMessage(query.error, defaults.loadMessagesError);
  }
  return {
    stored: query.data || NO_MESSAGES,
    isLoading: query.isLoading,
    error,
  };
};

const NO_UNANSWERED: PatientChatUnanswered[] = [];

/**
 * The questions of one conversation that got no answer, loaded only when the
 * gateway counted some. Cached like the messages: an ended conversation never
 * changes. A failure leaves them out (the answered questions still show).
 */
export const useConversationUnanswered = (
  agentGateway: AgentGateway,
  conversation: PatientConversationSummary | null,
): PatientChatUnanswered[] => {
  const conversationId = conversation?.id ?? null;
  const query = useQuery(
    maiaQueryKeys.unanswered(conversationId ?? -1),
    async () =>
      unwrap<PatientChatUnanswered[]>(
        await agentGateway.listPatientChatUnanswered(conversationId as number),
      ) || NO_UNANSWERED,
    {
      enabled:
        conversationId != null && (conversation?.unansweredCount ?? 0) > 0,
      staleTime: conversation?.endedAt ? Infinity : 0,
    },
  );
  return query.data || NO_UNANSWERED;
};
